import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { NgxSpinnerService } from 'ngx-spinner';
import { I18nKeys } from '@jump-tech-frontend/app-config';
import { ScrollService } from '../scroll.service';
import { AbstractDropdownQuestionComponent } from './abstract-dropdown-question.component';
import { checkIfExpression, untilDestroyed } from '@jump-tech-frontend/angular-common';
import { JUI_SELECT_IMPORTS, JuiAlertBlock } from '@jump-tech-frontend/core-components-angular';
import { QuestionHintComponent, QuestionLabelContentComponent } from '@jump-tech-frontend/question-components';
import { InlineSpinnerComponent } from '../inline-spinner-component/inline-spinner.component';
import { ReactiveFormsModule } from '@angular/forms';

import { getDataSourceData } from '../list-data-source';
import { CardOption } from '../../domain/question.base';
import { buildMessages } from './question.utils';

@Component({
  selector: `crds-question-dropdown`,
  template: `
    @if (optionsPopulated && show) {
      <div [formGroup]="form">
        @if (!initialised) {
          <div>
            @if (question.label) {
              <label class="form-label" [attr.for]="question.key">
                {{ question.label }}
                @if (question.required) {
                  <span> *</span>
                }
              </label>
            }
            <div class="form-control">
              <crds-inline-spinner
                [name]="question.key"
                [qaHook]="question.loadingQaHook"
                [type]="question.loadingType"
                [message]="question.loadingMessage"
                [colour]="question.loadingColour"
                [bgColour]="question.loadingBgColour"
                [textColour]="question.loadingTextColour"
                [center]="question.loadingCenter"
                >
              </crds-inline-spinner>
            </div>
          </div>
        }
        @if (initialised) {
          <div>
            @if (this.useJuiControls) {
              <jui-select
                size="sm"
                [attr.data-qa]="question.key"
                [multiple]="false"
                [formControlName]="question.key"
                [dataqastring]="question.key"
                [label]="question.label"
                [items]="juiSelectOptions"
                [disable]="form.get(question.key).disabled"
                [errors]="form.get(question.key)?.errors"
                [messages]="buildMessages(this.form, this.question, this.i18ns)"
                [placeholder]="question.placeholder ?? question.label"
                [validation]="'onFocus'"
              ></jui-select>
            } @else {
              @if (question.label) {
                <label class="form-label" [attr.for]="question.key">
                  <question-label-content
                    [question]="question"
                    [isInvalid]="isInvalid"
                    [i18ns]="i18ns"
                  ></question-label-content>
                </label>
              }
              <select
                [id]="question.key"
                [formControlName]="question.key"
                class="form-control mb-2"
                [attr.data-qa]="question.key"
                >
                @if (question.placeholder) {
                  <option [ngValue]="undefined" selected="selected" disabled>
                    {{ question.placeholder }}
                  </option>
                }
                @for (opt of currentFilter.length ? currentFilter : question.options; track opt) {
                  <option
                    [ngValue]="opt.key"
                    #dropDownInput
                    >
                    {{ opt.value }}
                  </option>
                }
              </select>
              <!-- QUESTION HINT -->
              <question-hint [question]="question"></question-hint>
              @if (alert) {
                <p>
                  <jui-alert-block iconName="info" size="sm">
                    {{ question.alert[form.get(question.key).value] }}
                  </jui-alert-block>
                </p>
              }
            }
          </div>
        }
      </div>
    }
    `,
  imports: [
    ReactiveFormsModule,
    InlineSpinnerComponent,
    QuestionLabelContentComponent,
    QuestionHintComponent,
    ...JUI_SELECT_IMPORTS,
    JuiAlertBlock
]
})
export class DropdownQuestionComponent extends AbstractDropdownQuestionComponent implements OnChanges {
  @Input() i18ns: I18nKeys;
  @Input() useJuiControls = false;
  private untilDestroyed = untilDestroyed();
  private sourceData = getDataSourceData();
  juiSelectOptions: { value: string; label: string }[] = [];

  constructor(scrollService: ScrollService, spinnerService: NgxSpinnerService) {
    super(scrollService, spinnerService);
  }

  async ngOnChanges(changes: SimpleChanges) {
    if (!this.question || !Object.prototype.hasOwnProperty.call(changes, 'question')) {
      return;
    }

    /**
     * Make sure that we check for null, undefined or a value that is not valid
     * If one of these then we should set the value to undefined so it can optionally trigger the placeholder <option>
     */

    if (this.question.datasource) {
      if (!this.question.datasource.transform) {
        this.question.datasource.transform =
          '[].{ text: join(`" - "`, [parent.name, name]), value: join(`" - "`, [parent.name, name]) }';
      }
      await this.spinnerService.show(this.question.key);
      this.mapData(
        await this.sourceData.getData(this.question.datasource),
        this.question.datasource,
        this.question,
        this.form
      );
      this.render();
      await this.spinnerService.hide(this.question.key);
    } else {
      this.render();
    }
  }

  render() {
    const options = this.question.options || [];
    const optionsIsString = typeof options === 'string'; // Using project type data - loaded post render in pathway

    if (
      !this.value ||
      (!optionsIsString &&
        !(this.question.options || []).some(o => o.key && this.value && o.key.toString() === this.value.toString()))
    ) {
      this.value = undefined;
    }
    this.initialised = true;

    this.currentFilter = Object.assign([], this.question.options);
    this.juiSelectOptions = this.currentFilter.length
      ? this.currentFilter.map(opt => {
          return {
            value: opt.key,
            label: opt.value
          };
        })
      : options.map(opt => {
          return {
            value: opt.key,
            label: opt.value
          };
        });
    this.scrollService.scrollObservable.pipe(this.untilDestroyed()).subscribe(documentId => {
      if (documentId === this.question.key) {
        this.input.nativeElement.scrollIntoView();
      }
    });
    this.setSubscriptions();
  }

  get optionsPopulated(): boolean {
    if (this.question.datasource) {
      return true;
    }
    return this.question.options && this.question.options.length > 0;
  }

  override get isInvalid() {
    return (this.form?.get(this.question.key)?.touched && this.form?.get(this.question.key)?.invalid) ?? true;
  }

  override get show() {
    const showIfQuestionPopulated = this.question.showIf ? this.showIf : true;
    return (
      (this.question.showIfPopulated !== true || this.value) && showIfQuestionPopulated && this.question.show !== false
    );
  }

  get alert() {
    return this.question.alert && this.question.alert[this.form?.get(this.question.key)?.value];
  }

  /**
   * DO NOT REMOVE - used dynamically by subscriptions "this[subscription.trigger]()"
   * Filter out question options based on the value of other form fields
   */
  private filterQuestionOptions() {
    this.currentFilter = Object.assign([], this.question.options ?? []).filter((option: CardOption) => {
      if (!option.filter) {
        return true;
      }
      return checkIfExpression(option.filter, this.form.value, this.form?.get(this.question.key)?.value);
    });
    this.juiSelectOptions = this.currentFilter.map(opt => {
      return {
        value: opt.key,
        label: opt.value
      };
    });
    const patchValue = this.currentFilter[this.getOptionValueIdx()].value;
    setTimeout(() => {
      this.form?.get(this.question.key)?.patchValue(patchValue);
    }, 100);
  }

  /**
   * If there is currently a value in this dropdown then return its index within the
   * current question.options
   * @return number valueIdx
   */
  private getOptionValueIdx() {
    const currentFormValue = this.form?.get(this.question.key)?.value;
    if (currentFormValue) {
      const idx = this.currentFilter.findIndex(function (filter) {
        return filter.value === currentFormValue;
      });
      return idx > -1 ? idx : 0;
    }
    return 0;
  }

  private setSubscriptions() {
    if (this.question.subscriptions) {
      this.question.subscriptions.forEach(subscription => {
        this[subscription.trigger]();
        this.form.controls[subscription.key].valueChanges.pipe(this.untilDestroyed()).subscribe(() => {
          this[subscription.trigger]();
        });
      });
    }
  }

  protected readonly buildMessages = buildMessages;
}
